import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  answerCallUp,
  type CallUpAnsweredRead,
  type CallUpAnswer,
  type TeamSheetRead,
} from '@volee/api-client'
import type { CallUpReplyState } from '@volee/ui'

import { teamSheetKey } from './lineup-team-sheet.ts'
import { messages } from '../messages/index.ts'
import { api } from '../services/api.ts'

// «Ci sono» and «Non ci sono»: what a convocato, or a tutore of theirs, tells the coach. The row lives
// in the sheet every page reads, so the answer stored is written into that sheet and none refetches.

/** A row of the convocazione as the reader got it: with its answer when they are entitled to it, and
 *  without the key when they are not (docs/adr/0002). */
export type CallUpOfSheet = TeamSheetRead['call_ups'][number]

/** What the row says of coming, `null` while nobody has answered — or while the reader is not told. */
export function answerOf(callUp: CallUpOfSheet): CallUpAnswer | null {
  return 'answer' in callUp ? callUp.answer : null
}

/** Whether the reader was sent the answer of this row, which is a different thing from there being one. */
export function isAnswered(callUp: CallUpOfSheet): callUp is CallUpAnsweredRead {
  return 'answer' in callUp
}

/** The words a card says each answer in. `open` is «nobody has answered»: a fact on the card a coach reads
 *  («Senza risposta»), an invitation on the player's own («Rispondi»). */
export type ReplyWords = Readonly<Record<CallUpReplyState, string>>

/** The words the cards of a convocazione say each answer in: the coach's, the family's, the pass of whoever
 *  only reads, and the profile card of a follower. «Senza risposta» is a fact in all of them. */
export const CARD_REPLY_WORDS: ReplyWords = {
  coming: messages.match.callUp.markComing,
  not_coming: messages.match.callUp.markNotComing,
  open: messages.match.callUp.markOpen,
}

/** The answer of a row as a card draws it: the state, and the words for it. The one place an answer, which
 *  is `coming`, `not_coming` or nothing, becomes the three states a card knows. */
export function replyOf(
  answer: CallUpAnswer | null,
  words: ReplyWords,
): { readonly state: CallUpReplyState; readonly label: string } {
  const state = answer ?? 'open'
  return { state, label: words[state] }
}

export interface Reply {
  readonly callUpId: number
  readonly answer: CallUpAnswer
}

export function useAnswerCallUp(matchId: number, enrollmentId: number) {
  const cache = useQueryClient()
  return useMutation({
    mutationFn: ({ callUpId, answer }: Reply) => answerCallUp(api, matchId, callUpId, answer),
    onSuccess: (updated) => {
      cache.setQueryData<TeamSheetRead>(teamSheetKey(matchId, enrollmentId), (sheet) =>
        sheet === undefined
          ? sheet
          : {
              ...sheet,
              call_ups: sheet.call_ups.map((row) => (row.id === updated.id ? updated : row)),
            },
      )
    },
  })
}
